import React, { useCallback } from "react";
import { useSocketEvent, WhatsAppNotificationEvent } from "./SocketContext";
import { useNotification } from "./NotificationProvider";
import {
  getBrowserNotificationClickUrl,
  loadBrowserNotifyEventPrefs,
  isWhatsAppCallReceivedPayload,
  isEmptyCallReceivedPayload
} from "../../constants/browserNotifications";
import { TranslationService } from "../../services/TranslationService";
import CompanyService from "../../services/CompanyService";

/**
 * Escucha CallReceived del socket y muestra notificación del navegador si hay permiso
 * y la preferencia whatsappIncoming está activa. Montar bajo SocketProvider y NotificationProvider.
 */
export const BrowserNotificationSocketBridge: React.FC = () => {
  const { permission, showBrowserNotification } = useNotification();

  const notifyCallReceived = useCallback(
    async (payload: any): Promise<string> => {
      if (permission !== "granted")
        return `omitida: permiso en contexto = "${permission}" (Notification.permission = "${Notification.permission}")`;
      if (!CompanyService.canUseWhatsappNotifications())
        return "omitida: canUseWhatsappNotifications() = false";

      const prefs = loadBrowserNotifyEventPrefs();
      if (!prefs.whatsappIncoming)
        return "omitida: preferencia whatsappIncoming desactivada";

      const isEmptyPayload = isEmptyCallReceivedPayload(payload);
      if (!isEmptyPayload && !isWhatsAppCallReceivedPayload(payload))
        return "omitida: el payload no es Type 6 (WhatsApp)";

      // Notificar si la pestaña está oculta o si la ventana perdió el foco (otra app al frente)
      if (!document.hidden && document.hasFocus())
        return "omitida: la pestaña estaba visible y con foco";

      // Sin datos del mensaje: notificación genérica con tag único para que no reemplace a la anterior
      if (isEmptyPayload) {
        const tag = `whatsapp-call-${Date.now()}`;
        await showBrowserNotification({
          title: TranslationService.translate.BrowserNotifyWhatsAppTitle,
          body: TranslationService.translate.BrowserNotifyWhatsAppNewMessage,
          data: { type: "whatsapp", url: getBrowserNotificationClickUrl() },
          tag
        });
        return `enviada genérica (tag: ${tag})`;
      }

      const personName =
        payload?.PersonName ||
        payload?.personName ||
        TranslationService.translate.BrowserNotifyWhatsAppDefaultContact;
      const preview =
        payload?.Content ||
        payload?.content ||
        payload?.Subject ||
        payload?.subject ||
        TranslationService.translate.BrowserNotifyWhatsAppNewMessage;

      const personId =
        payload?.PersonID ?? payload?.personId ?? payload?.PersonId;
      const messageId = payload?.MessageID ?? payload?.messageId ?? payload?.ID;
      const tag = `whatsapp-${personId ?? "unknown"}-${messageId ?? "latest"}`;

      await showBrowserNotification({
        title: TranslationService.translate.BrowserNotifyWhatsAppTitle,
        body: `${personName}: ${preview}`,
        data: {
          ...payload,
          type: "whatsapp",
          personId,
          messageId,
          url: getBrowserNotificationClickUrl(personId)
        },
        tag
      });
      return `enviada (tag: ${tag})`;
    },
    [permission, showBrowserNotification]
  );

  const handleCallReceived = useCallback(
    async (payload: any) => {
      let result = "error al notificar (ver consola)";
      try {
        result = await notifyCallReceived(payload);
        console.log(
          `Notificación de llamada entrante de WhatsApp: ${result}`,
          payload
        );
      } catch (error) {
        console.error(
          "Error al notificar llamada entrante de WhatsApp:",
          error
        );
      }
    },
    [notifyCallReceived]
  );

  useSocketEvent(WhatsAppNotificationEvent.CallReceived, handleCallReceived);

  return null;
};
